import { Collapse, Icon, IconButton } from '@mui/material'
import { getRoute } from 'helpers/route.helper/route.helper'
import React, { ReactElement, ReactNode, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { dynamicClassName } from 'styles/helper'
import ErrorFallback from 'ui/components/ErrorFallback/ErrorFallback'
import Title from 'ui/molecules/Title/Title'
import sentry from 'utils/sentry/sentry'
import styles from './Card.module.less'

type CardProps = {
  children: ReactNode | ReactElement | ReactNode[] | ReactElement[] | null | string
  collapsible?: boolean
  defaultCollapsed?: boolean
  rememberCollapsed?: boolean
  title?: string
  titleSize?: 'small' | 'medium' | 'large'
  titleEndAdornment?: ReactNode
  width?: string
  height?: string
  tourId?: string
  style?: 'default' | 'table' | 'sub' // Table and sub are different style containers that you can use. Table is useful when you want to have several collapsible views in the same card, and sub is another collapsible view that can be used inside the table view. This is usually used in UI config.
  ignoreMargin?: boolean
}

export default function Card({
  children,
  collapsible,
  defaultCollapsed,
  rememberCollapsed,
  title,
  titleSize,
  titleEndAdornment,
  width,
  height,
  tourId,
  style,
  ignoreMargin = false,
}: CardProps): ReactElement {
  const route = getRoute()

  const key = `${route}_${title}`

  const cardStyle: Record<string, string> = {}

  const localStorageItem = key ? localStorage.getItem(key) : null
  const isLocalStorageCollapsed = localStorageItem
    ? localStorageItem === 'open'
      ? false
      : localStorageItem === 'close'
        ? true
        : null
    : null

  const [collapsed, setCollapsed] = useState<boolean>(
    rememberCollapsed && isLocalStorageCollapsed !== null ? isLocalStorageCollapsed : (defaultCollapsed ?? false)
  )

  if (width) {
    cardStyle.width = width
  }

  if (height) {
    cardStyle.height = height
  }

  const optionalClassNames: Record<string, boolean> = {
    [styles.Card]: style === 'default' || !style,
    [styles.IgnoreMargin]: ignoreMargin,
    [styles.CardStyle_table]: style === 'table',
    [styles.CardStyle__sub]: style === 'sub',
  }
  if (tourId) {
    optionalClassNames[tourId] = true
  }

  const content = (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error) => sentry.captureBoundaryException(error, { boundaryName: 'CardContent' })}
    >
      {children}
    </ErrorBoundary>
  )

  return (
    <div className={dynamicClassName(optionalClassNames)} style={cardStyle}>
      {collapsible ? (
        <div
          className={dynamicClassName({
            [styles.CollapseTitleContainer]: true,
            [styles.CollapseTitleContainer__grey]: style === 'table',
          })}
          role="button"
          aria-label="collapse card"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation()
            setCollapsed(!collapsed)
            if (rememberCollapsed && key) {
              localStorage.setItem(key, collapsed ? 'open' : 'close')
            }
          }}
        >
          {title &&
            (titleEndAdornment ? (
              <div className={styles.TitleAdornmentRow}>
                <Title title={title} size={titleSize} marginLeft />
                {titleEndAdornment}
              </div>
            ) : (
              <Title title={title} size={titleSize} marginLeft />
            ))}
          <div
            className={dynamicClassName({
              [styles.CollapseIconContainer]: true,
              [styles.CollapseIconContainer__sub]: style === 'sub',
            })}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                setCollapsed(!collapsed)
                if (rememberCollapsed && key) {
                  localStorage.setItem(key, collapsed ? 'open' : 'close')
                }
              }}
              size="large"
            >
              <Icon fontSize="small" className={collapsed ? 'fal fa-chevron-right' : 'fal fa-chevron-down'} />
            </IconButton>
          </div>
        </div>
      ) : (
        <>
          {title &&
            (titleEndAdornment ? (
              <div className={styles.TitleAdornmentRow}>
                <Title title={title} size={titleSize} marginLeft />
                {titleEndAdornment}
              </div>
            ) : (
              <Title title={title} size={titleSize} marginLeft />
            ))}
        </>
      )}

      {collapsible ? <Collapse in={!collapsed}>{!collapsed && content}</Collapse> : content}
    </div>
  )
}
